import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import { getMatchingFilterFromRange } from 'lib/experiences/experienceSearchFilterUtils'
import { without } from 'lib/array/arrayUtils'
import useExperiencesSearchFilters from './useExperiencesSearchFilters'

export interface ActiveFilterChip {
  key: string
  label: string
  onRemove: () => void
}

const LUX_PLUS_CATEGORY_IDS = [EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID]

function useActiveExperienceFilterChips(onAfterDateRemove?: () => void): Array<ActiveFilterChip> {
  const intl = useIntl()
  const currencyFormatter = useCurrencyFormatter()
  const { searchFilters, setSearchFilters, setCategoryCodesSearchFilter } = useExperiencesSearchFilters()
  const categories = useAppSelector(state => state.experience.experienceCategories)

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const category of categories) {
      map.set(category.id, category.name)
    }
    return map
  }, [categories])

  return useMemo(() => {
    const chips: Array<ActiveFilterChip> = []

    const activeDateFilter = searchFilters.date
      ?? getMatchingFilterFromRange(searchFilters.checkIn, searchFilters.checkOut)
    if (activeDateFilter && activeDateFilter !== 'anytime') {
      const dateLabels: Partial<Record<string, string>> = {
        today: intl.formatMessage({
          id: 'AHwE-JrMwGjmJsScIJJ8',
          defaultMessage: 'Today',
          description: 'Quick date chip label for today in experiences date dropdown',
        }),
        tomorrow: intl.formatMessage({
          id: 'IdXFYyn3Ih3xMMiAMWJ1',
          defaultMessage: 'Tomorrow',
          description: 'Quick date chip label for tomorrow in experiences date dropdown',
        }),
        'this-weekend': intl.formatMessage({
          id: 'AzBOEGuHa1-HP1JDqYkD',
          defaultMessage: 'This weekend',
          description: 'Quick date chip label for this weekend in experiences date dropdown',
        }),
      }
      const label = dateLabels[activeDateFilter]
      if (label) {
        chips.push({
          key: `date-${activeDateFilter}`,
          label,
          onRemove: () => {
            setSearchFilters({ ...searchFilters, date: undefined, checkIn: undefined, checkOut: undefined })
            onAfterDateRemove?.()
          },
        })
      }
    }

    if (searchFilters.minRating) {
      chips.push({
        key: `rating-${searchFilters.minRating}`,
        label: intl.formatMessage(
          {
            id: '1ibx0Pa6PegLZuMET_08',
            defaultMessage: '{rating}.0+ rating',
            description: 'Rating filter chip label showing minimum rating threshold in experience search',
          },
          { rating: searchFilters.minRating },
        ),
        onRemove: () => setSearchFilters({ ...searchFilters, minRating: undefined }),
      })
    }

    if (searchFilters.priceGte || searchFilters.priceLte) {
      const parts: Array<string> = []
      if (searchFilters.priceGte) {
        parts.push(intl.formatMessage(
          {
            id: 'JX7yTDLY6quhbUH-HQ9h',
            defaultMessage: '{price}+',
            description: 'Price filter chip label showing minimum price in experience search',
          },
          { price: currencyFormatter(searchFilters.priceGte) },
        ))
      }
      if (searchFilters.priceLte) {
        parts.push(intl.formatMessage(
          {
            id: 'I7GjIDDs0Ut5Uzu4a5EA',
            defaultMessage: 'up to {price}',
            description: 'Price filter chip label showing maximum price in experience search',
          },
          { price: currencyFormatter(searchFilters.priceLte) },
        ))
      }
      chips.push({
        key: 'price',
        label: parts.join(' '),
        onRemove: () => setSearchFilters({ ...searchFilters, priceGte: undefined, priceLte: undefined }),
      })
    }

    searchFilters.categoryCodes
      ?.filter(id => !LUX_PLUS_CATEGORY_IDS.includes(id))
      .forEach(id => {
        const name = categoryNameById.get(id)
        if (name) {
          chips.push({
            key: `category-${id}`,
            label: name,
            onRemove: () => setCategoryCodesSearchFilter(without(searchFilters.categoryCodes ?? [], id)),
          })
        }
      })

    const featuredLabels: Partial<Record<Experiences.FeaturedFilter, string>> = {
      'free-cancellation': intl.formatMessage({
        id: 'gnc42bYCUYEC1VtZ_FBb',
        defaultMessage: 'Free cancellation',
        description: 'Form field label for free cancellation policy on experience',
      }),
      'instant-confirmation': intl.formatMessage({
        id: 'liiEI8aOkzbI4FNe-4v4',
        defaultMessage: 'Instant confirmation',
        description: 'Featured filter option for instant confirmation in experience search',
      }),
      'is-gift': intl.formatMessage({
        id: 'kl0s-nwUJycMsemZfm-u',
        defaultMessage: 'Gift inspiration',
        description: 'Featured filter chip label for gift inspiration in experience search',
      }),
    }
    searchFilters.featured?.forEach(featured => {
      chips.push({
        key: `featured-${featured}`,
        label: featuredLabels[featured] ?? featured,
        onRemove: () => setSearchFilters({
          ...searchFilters,
          featured: searchFilters.featured?.filter(featuredFilter => featuredFilter !== featured),
        }),
      })
    })

    if (searchFilters.leExclusive) {
      chips.push({
        key: 'le-exclusive',
        label: intl.formatMessage({
          id: 'TxlMMKgor5yZq-d8fA7x',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        }),
        onRemove: () => setSearchFilters({ ...searchFilters, leExclusive: undefined }),
      })
    }

    if (searchFilters.luxPlusFeatures?.length) {
      chips.push({
        key: 'luxplus',
        label: 'Lux Plus',
        onRemove: () => setSearchFilters({ ...searchFilters, luxPlusFeatures: undefined }),
      })
    }

    const travelStyleLabels: Partial<Record<Experiences.TravelStyleFilter, string>> = {
      'family-friendly': intl.formatMessage({
        id: 'A2Rii617VkKkEqZgSTXB',
        defaultMessage: 'Family friendly',
        description: 'Travel style filter option for family friendly in experience search',
      }),
      'couples': intl.formatMessage({
        id: 'LaA0PgRiBlfdYL43-IMC',
        defaultMessage: 'Couples',
        description: 'Traveller type filter label for couples in the search or listing filters panel',
      }),
      'large-groups': intl.formatMessage({
        id: 'lJH8OZuJxWjWNnh4laP_',
        defaultMessage: 'Large groups',
        description: 'Travel style filter option for large groups in experience search',
      }),
      'solo-explorers': intl.formatMessage({
        id: 'ymauUvM_REtis5IuuLHz',
        defaultMessage: 'Solo explorers',
        description: 'Travel style filter option for solo explorers in experience search',
      }),
    }
    searchFilters.travelStyles?.forEach(travelStyle => {
      chips.push({
        key: `style-${travelStyle}`,
        label: travelStyleLabels[travelStyle] ?? travelStyle,
        onRemove: () => setSearchFilters({
          ...searchFilters,
          travelStyles: searchFilters.travelStyles?.filter(s => s !== travelStyle),
        }),
      })
    })

    return chips
  }, [searchFilters, setSearchFilters, setCategoryCodesSearchFilter, categoryNameById, intl, currencyFormatter, onAfterDateRemove])
}

export default useActiveExperienceFilterChips
